import { atom } from 'jotai'
import { pageIdAtom } from './pageAtoms'
import type { PageBody, PageCard, PageDefinition, PageHints } from './pageTypes'

/*
 * The kit's view of the pages: the catalog (src/pages/catalog.ts) registers every page's card and a
 * loader for its body once at startup, so the kit can find the open page's definition (its hints)
 * from the document alone, without importing any page. Engine state that follows from a page (its
 * protected background) is derived from the document's page id, never written during render.
 *
 * Bodies load on demand (`loadPage`): the document loader (src/hooks/useDocumentLoader.ts) waits for
 * a page document's body before the document reaches the store, so by the time anything reads
 * `currentPageAtom` for it, the page is here.
 */

/** A page as registered: its card, and how to load its body. */
export type RegisteredPage = { card: PageCard; load: () => Promise<PageBody> }

let entries: readonly RegisteredPage[] = []
let cards: readonly PageCard[] = []
let byId = new Map<string, RegisteredPage>()
let loaded = new Map<string, PageDefinition>()
let loading = new Map<string, Promise<PageDefinition | null>>()

/** Called once by the catalog with every page; a later call replaces the list (hot reload, tests). */
export function registerPages(list: readonly RegisteredPage[]): void {
  entries = list
  cards = list.map((entry) => entry.card)
  byId = new Map(list.map((entry) => [entry.card.id, entry]))
  loaded = new Map()
  loading = new Map()
}

/** Every registered page as registered (to put the registry back after a test). */
export function registeredPageEntries(): readonly RegisteredPage[] {
  return entries
}

/** Every registered page's card, in home-screen order. */
export function registeredPages(): readonly PageCard[] {
  return cards
}

export function registeredPageById(id: string | null | undefined): PageCard | null {
  return id ? byId.get(id)?.card ?? null : null
}

/** The page with its body, once loaded (`loadPage`); null for an unknown page or one not loaded yet. */
export function loadedPageById(id: string | null | undefined): PageDefinition | null {
  return id ? loaded.get(id) ?? null : null
}

/** Load a page's body (once; later calls share it). Null for an id no page has (a removed page). */
export function loadPage(id: string): Promise<PageDefinition | null> {
  const done = loaded.get(id)
  if (done) return Promise.resolve(done)
  const pending = loading.get(id)
  if (pending) return pending
  const entry = byId.get(id)
  if (!entry) return Promise.resolve(null)
  const registry = loaded
  const promise = entry.load().then((body) => {
    const page: PageDefinition = { ...entry.card, ...body }
    registry.set(id, page)
    return page
  })
  loading.set(id, promise)
  return promise
}

const NO_HINTS: PageHints = Object.freeze({})
const NO_TYPES: readonly string[] = Object.freeze([])

/** The page owning the current document, or null (ordinary document, or a page that no longer exists). */
export const currentPageAtom = atom((get) => loadedPageById(get(pageIdAtom)))

/** The current page's engine hints (empty for ordinary documents). */
export const currentPageHintsAtom = atom((get) => get(currentPageAtom)?.hints ?? NO_HINTS)

/**
 * Voxel types the current page's background is made of (`hints.protect`): every spade on the page
 * leaves them alone (and its preview never shows them going); setDigTool merges them into the
 * eraser's protect list.
 */
export const pageProtectTypesAtom = atom((get) => get(currentPageHintsAtom).protect ?? NO_TYPES)
